/**
 * CustomerCancellationModal
 * Modal for handling customer cancellation of planned assignments.
 * Notifies assigned TGs and resets status to preliminary.
 */

import { useState } from 'react'
import { X, Loader2, Leaf, MessageSquare, ChevronDown, ChevronRight, XCircle, Calendar } from 'lucide-react'
import { format } from 'date-fns'
import type { TgWorkStatus } from '../../types/statusTypes'

interface TgInfo {
  id: string
  subcontractorId: string
  name: string
  phone: string | null
  currentStatus: TgWorkStatus
}

export interface CancellationResult {
  reason: string
  date: string
  sendSmsTo: Record<string, boolean>  // keyed by assignment_subcontractor id
}

interface CustomerCancellationModalProps {
  isOpen: boolean
  onClose: () => void
  assignmentNumber?: string
  assignmentTitle: string
  assignmentDate: string
  assignmentAddress?: string
  startTime?: string | null
  customerName: string
  tgList: TgInfo[]
  onConfirm: (result: CancellationResult) => Promise<void>
}

export function CustomerCancellationModal({
  isOpen,
  onClose,
  assignmentNumber,
  assignmentTitle,
  assignmentDate,
  assignmentAddress,
  startTime,
  customerName,
  tgList,
  onConfirm
}: CustomerCancellationModalProps) {
  const [reason, setReason] = useState('')
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [sendSmsTo, setSendSmsTo] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    tgList.forEach(tg => {
      // Default to true if TG has phone number
      initial[tg.id] = !!tg.phone
    })
    return initial
  })
  const [expandedSms, setExpandedSms] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const toggleSendSms = (tgId: string) => {
    setSendSmsTo(prev => ({ ...prev, [tgId]: !prev[tgId] }))
  }

  const toggleSmsExpanded = (tgId: string) => {
    setExpandedSms(prev => ({ ...prev, [tgId]: !prev[tgId] }))
  }

  const handleConfirm = async () => {
    if (!reason.trim()) {
      alert('Ange avbokningsorsak')
      return
    }

    setIsSubmitting(true)
    try {
      await onConfirm({
        reason: reason.trim(),
        date: selectedDate,
        sendSmsTo
      })
      onClose()
    } catch (error) {
      console.error('Error confirming cancellation:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  const getTgStatusLabel = (status: TgWorkStatus) => {
    switch (status) {
      case 'preliminar_tilldelad': return 'Preliminärt tilldelad'
      case 'tilldelad': return 'Tilldelad'
      case 'ej_rapporterad': return 'Ej rapporterad'
      case 'rapporterad': return 'Rapporterad'
      case 'komplettering': return 'Komplettering'
      case 'godkand': return 'Godkänd'
      case 'avsagt': return 'Avsagt'
      default: return status
    }
  }

  // Format time to HH:MM (remove seconds if present)
  const formatTime = (time: string | null | undefined) => {
    if (!time) return ''
    return time.substring(0, 5)
  }

  // Format assignment identifier
  const getAssignmentIdentifier = () => {
    if (assignmentNumber) {
      return `#${assignmentNumber} ${assignmentTitle}`
    }
    return assignmentTitle
  }

  // Format date with time
  const formatDateTime = () => {
    const formattedTime = formatTime(startTime)
    if (formattedTime) {
      return `${assignmentDate} ${formattedTime}`
    }
    return assignmentDate
  }

  // Get SMS message for TG
  const getSmsMessage = () => {
    const identifier = getAssignmentIdentifier()
    const dateTime = formatDateTime()
    const addressPart = assignmentAddress ? `${assignmentAddress}, ` : ''
    return `Uppdraget ${identifier} (${addressPart}planerat ${dateTime}) har avbokats. ${reason.trim() || 'Ingen orsak angiven.'}`
  }

  // Count total SMS to be sent
  const getTotalSmsCount = () => {
    return tgList.filter(tg => tg.phone && sendSmsTo[tg.id]).length
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                  Kundavbokning
                </h3>
                <p className="text-sm text-nature-500 dark:text-nature-400">
                  {customerName} avbokar uppdraget
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="px-6 py-4 space-y-5 overflow-y-auto flex-1">
          {/* Assignment info */}
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-sm text-red-800 dark:text-red-200">
              <span className="font-medium">{getAssignmentIdentifier()}</span>
              <span className="mx-2">•</span>
              <span>{formatDateTime()}</span>
            </p>
            <p className="text-xs text-red-600 dark:text-red-400 mt-2">
              Status återställs till <strong>Preliminär</strong> och alla TG:s blir <strong>Preliminärt tilldelade</strong>
            </p>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-nature-700 dark:text-nature-300">
              Anledning till avbokning *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ange anledning till avbokningen..."
              rows={3}
              className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100 text-sm"
              autoFocus
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
              <Calendar className="h-4 w-4 inline mr-1" />
              Avbokningsdatum
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100 text-sm"
            />
          </div>

          {/* TG notification section */}
          {tgList.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-nature-700 dark:text-nature-300">
                <Leaf className="h-4 w-4" />
                <span className="font-medium">Meddela trädgårdsmästare</span>
                <span className="text-sm text-nature-500">({tgList.length} tilldelade)</span>
              </div>

              <div className="space-y-3 pl-6">
                {tgList.map(tg => {
                  const canSendSms = !!tg.phone
                  const willSendSms = canSendSms && sendSmsTo[tg.id]
                  const isExpanded = expandedSms[tg.id]

                  return (
                    <div key={tg.id} className="border border-nature-200 dark:border-nature-700 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-nature-800 dark:text-nature-200">{tg.name}</span>
                          <span className="text-xs px-2 py-0.5 rounded bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-400">
                            {getTgStatusLabel(tg.currentStatus)}
                          </span>
                        </div>
                        {/* SMS indicator */}
                        {canSendSms && (
                          <button
                            type="button"
                            onClick={() => toggleSmsExpanded(tg.id)}
                            className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                              willSendSms
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                : 'bg-nature-100 text-nature-500 dark:bg-nature-700 dark:text-nature-400'
                            }`}
                          >
                            <MessageSquare className="h-3 w-3" />
                            <span>{willSendSms ? 'SMS' : 'Inget SMS'}</span>
                            {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          </button>
                        )}
                        {!tg.phone && (
                          <span className="text-xs text-nature-400 dark:text-nature-500 italic">
                            Inget telefonnummer
                          </span>
                        )}
                      </div>

                      {/* SMS Preview - collapsible */}
                      {canSendSms && isExpanded && (
                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                              SMS till {tg.phone}
                            </span>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={sendSmsTo[tg.id] || false}
                                onChange={() => toggleSendSms(tg.id)}
                                className="h-3.5 w-3.5 text-primary-600 rounded"
                              />
                              <span className="text-xs text-blue-600 dark:text-blue-400">Skicka</span>
                            </label>
                          </div>
                          <div className={`bg-white dark:bg-nature-800 rounded p-2 border border-blue-200 dark:border-blue-700 ${!sendSmsTo[tg.id] ? 'opacity-50' : ''}`}>
                            <p className="text-xs text-nature-700 dark:text-nature-300 font-mono">
                              {getSmsMessage()}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {tgList.length === 0 && (
            <div className="text-center py-4 text-nature-500 dark:text-nature-400 text-sm italic">
              Inga trädgårdsmästare tilldelade - ingen SMS-notifiering behövs
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900 flex-shrink-0">
          <div className="flex items-center justify-between">
            {/* SMS summary */}
            <div className="text-sm text-nature-500 dark:text-nature-400">
              {getTotalSmsCount() > 0 ? (
                <span className="flex items-center gap-1">
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                  {getTotalSmsCount()} SMS skickas
                </span>
              ) : (
                <span className="italic">Inga SMS skickas</span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="btn-outline px-4 py-2"
                disabled={isSubmitting}
              >
                Avbryt
              </button>
              <button
                onClick={handleConfirm}
                disabled={isSubmitting || !reason.trim()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg flex items-center space-x-2 transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Sparar...</span>
                  </>
                ) : (
                  <span>Bekräfta avbokning</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
